import { hc } from "hono/client";
import type { InferResponseType } from "hono/client";
import type { AppType } from "@omina/api";

/**
 * The typed RPC client.
 *
 * `AppType` is imported from the API's source through a tsconfig path alias
 * rather than a package dependency. That is deliberate: apps/api already
 * depends on @omina/app (its Worker serves ../app/dist as static assets), so a
 * package dependency in this direction would make the two apps a cycle in the
 * build graph — and app must build *before* api, always.
 *
 * The base URL is "/" because the SPA and the API are one Worker on one
 * origin. No CORS, no configured API host, and session cookies are first-party
 * — fetch sends them by default on same-origin requests.
 */
export const client = hc<AppType>("/");

/** Everything lives under /api; this saves writing it at every call site. */
export const api = client.api;

// ─────────────────────────────────────────────────────────────
// Compile-time guard: the client must not be `any`.
//
// Mirrors apps/api/test/api.test.ts. The API's routes are declared as one
// chained expression precisely so `AppType` keeps inferring; if someone splits
// that chain — or if the type bridge above silently stops resolving — `hc`
// degrades to `any` and every call in this app stops being checked without a
// single error appearing. A structural check alone passes against `any`, so
// the `IsAny` probe is the point.
//
// `pnpm typecheck` runs this file. These aliases are the test.
// ─────────────────────────────────────────────────────────────

type IsAny<T> = 0 extends 1 & T ? true : false;
type Assert<T extends true> = T;

export type MeResponse = InferResponseType<typeof api.me.$get, 200>;
export type ProfileResponse = InferResponseType<typeof api.profile.$get, 200>;
export type SettingsResponse = InferResponseType<typeof api.settings.$get, 200>;
export type LoopSummaryResponse = InferResponseType<
  (typeof api.loop.summary)["$get"],
  200
>;
export type ImportResponse = InferResponseType<typeof api.import.$post, 200>;

type _MeIsNotAny = Assert<IsAny<MeResponse> extends true ? false : true>;
type _SettingsIsNotAny = Assert<IsAny<SettingsResponse> extends true ? false : true>;
type _SummaryIsNotAny = Assert<IsAny<LoopSummaryResponse> extends true ? false : true>;
type _ImportIsNotAny = Assert<IsAny<ImportResponse> extends true ? false : true>;

// Exported so the aliases above are referenced rather than dead.
export type { _MeIsNotAny, _SettingsIsNotAny, _SummaryIsNotAny, _ImportIsNotAny };

/**
 * Turn a non-2xx RPC response into an Error worth showing.
 *
 * The API answers errors as `{ error, message?, issues? }`. Surfacing that
 * text is the difference between "something went wrong" and "finish your
 * intake first" — the latter is the only one a founder can act on.
 */
export async function apiError(res: Response): Promise<Error> {
  let detail = `${res.status}`;
  try {
    const body = (await res.json()) as {
      error?: string;
      message?: string;
      issues?: string[];
    };
    detail = body.message ?? body.issues?.join("; ") ?? body.error ?? detail;
  } catch {
    // A non-JSON error body (a proxy, an HTML error page). The status stands.
  }
  return new Error(detail);
}
